import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button, PageHeader } from '@/components/ui'
import { adminApi } from '@/lib/api'
import { ArrowLeft, Key, Power, RotateCcw, Copy, Check, Plus, ExternalLink, LogIn, BarChart3, Eye } from 'lucide-react'

export const Route = createFileRoute('/_app/tenants/$id')({
  component: TenantDetailPage,
})

interface TenantDetail {
  id: string
  slug: string
  name: string
  primaryHostname: string | null
  storefrontUrl: string | null
  status: 'active' | 'past_due' | 'suspended' | 'canceled'
  defaultCurrency: string
  defaultLocale: string
  branding: Record<string, unknown>
  metadata: Record<string, string>
  createdAt: string
  updatedAt: string
}

interface TenantUsage {
  period: string
  plan: string
  currentPeriodEnd: string | null
  limits: {
    apiRequestsPerMonth: number
    productsMax: number
    ordersPerMonthMax: number
    storageMaxGB: number
    bandwidthMaxGB: number
    teamSeatsMax: number
  }
  usage: {
    apiRequests: { used: number; max: number; pct: number }
    orders: { used: number; max: number; pct: number }
    bandwidthGB: { used: number; max: number; pct: number }
    storageGB: { used: number; max: number; pct: number }
  }
}

interface TenantStats {
  products: number
  orders: { total: number; thisMonth: number }
  customers: number
  apiKeys: number
}

interface ApiKey {
  id: string
  name: string
  prefix: string
  environment: 'live' | 'test'
  lastUsedAt: string | null
  createdAt: string
  revokedAt: string | null
}

function TenantDetailPage() {
  const { id } = Route.useParams()
  const qc = useQueryClient()
  const [generatedKey, setGeneratedKey] = useState<string | null>(null)

  const { data: tenant } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => adminApi.get<{ data: TenantDetail }>(`/api/admin/tenants/${id}`),
  })

  const { data: stats } = useQuery({
    queryKey: ['tenant-stats', id],
    queryFn: () => adminApi.get<{ data: TenantStats }>(`/api/admin/tenants/${id}/stats`),
  })

  const { data: usage } = useQuery({
    queryKey: ['tenant-usage', id],
    queryFn: () => adminApi.get<{ data: TenantUsage }>(`/api/admin/tenants/${id}/usage`),
    refetchInterval: 30_000,
  })

  const { data: apiKeysData } = useQuery({
    queryKey: ['tenant-api-keys', id],
    queryFn: () => adminApi.get<{ data: ApiKey[] }>(`/api/admin/api-keys/tenant/${id}`),
  })

  const suspendMutation = useMutation({
    mutationFn: () => adminApi.post(`/api/admin/tenants/${id}/suspend`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', id] }),
  })

  const reactivateMutation = useMutation({
    mutationFn: () => adminApi.post(`/api/admin/tenants/${id}/reactivate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', id] }),
  })

  const createKeyMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ data: { fullKey: string } }>('/api/admin/api-keys', {
        tenantId: id,
        name: `Generated ${new Date().toISOString().slice(0, 10)}`,
        environment: 'live',
        scopes: ['*'],
      }),
    onSuccess: (res) => {
      setGeneratedKey(res.data.fullKey)
      qc.invalidateQueries({ queryKey: ['tenant-api-keys', id] })
    },
  })

  const revokeKeyMutation = useMutation({
    mutationFn: (keyId: string) => adminApi.delete(`/api/admin/api-keys/${keyId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-api-keys', id] }),
  })

  if (!tenant) return <div className="p-8 text-neutral-400">Cargando...</div>

  const t = tenant.data

  return (
    <>
      <div className="border-b border-neutral-800 px-8 py-4">
        <Link to="/tenants" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1">
          <ArrowLeft className="size-4" /> Volver a Tiendas
        </Link>
      </div>
      <PageHeader
        title={t.name}
        description={`${t.slug} · ${t.defaultCurrency} · ${t.defaultLocale}`}
        action={
          <div className="flex gap-2">
            {t.status === 'active' ? (
              <Button variant="destructive" size="sm" onClick={() => suspendMutation.mutate()} disabled={suspendMutation.isPending}>
                <Power className="size-4 mr-2" /> Suspender
              </Button>
            ) : (
              <Button variant="default" size="sm" onClick={() => reactivateMutation.mutate()} disabled={reactivateMutation.isPending}>
                <RotateCcw className="size-4 mr-2" /> Reactivar
              </Button>
            )}
          </div>
        }
      />

      <div className="p-8 space-y-6">
        {/* Status row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={t.status === 'active' ? 'success' : t.status === 'suspended' ? 'destructive' : 'warning'}>
            {t.status}
          </Badge>
          {usage && <Badge variant="muted">{usage.data.plan}</Badge>}
          {t.metadata.industry && <Badge variant="muted">{t.metadata.industry}</Badge>}
          {t.metadata.country && <Badge variant="muted">{t.metadata.country}</Badge>}
        </div>

        {/* Acciones principales — botones grandes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/store/$id" params={{ id }} className="block group">
            <Card className="border-purple-800/40 hover:border-purple-700 transition cursor-pointer h-full">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="size-10 rounded bg-purple-900/40 flex items-center justify-center shrink-0">
                    <LogIn className="size-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-semibold">Entrar al backoffice</p>
                    <p className="text-xs text-neutral-400 mt-1">
                      Ver y gestionar la tienda como soporte. Modo soporte con banner visible.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <a href="#usage-section" className="block group">
            <Card className="hover:border-neutral-700 transition cursor-pointer h-full">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="size-10 rounded bg-blue-900/40 flex items-center justify-center shrink-0">
                    <BarChart3 className="size-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-semibold">Ver consumo</p>
                    <p className="text-xs text-neutral-400 mt-1">
                      API requests, bandwidth, storage del mes vs límites del plan.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </a>

          {t.storefrontUrl ? (
            <a
              href={t.storefrontUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
            >
              <Card className="hover:border-neutral-700 transition cursor-pointer h-full">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="size-10 rounded bg-emerald-900/40 flex items-center justify-center shrink-0">
                      <Eye className="size-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-semibold">Visitar storefront</p>
                      <p className="text-xs text-neutral-400 mt-1 truncate">{t.storefrontUrl}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>
          ) : (
            <Link to="/store/$id/settings" params={{ id }} className="block group">
              <Card className="hover:border-neutral-700 transition cursor-pointer h-full">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="size-10 rounded bg-neutral-800 flex items-center justify-center shrink-0">
                      <Eye className="size-5 text-neutral-500" />
                    </div>
                    <div>
                      <p className="font-semibold">Configurar storefront URL</p>
                      <p className="text-xs text-neutral-500 mt-1">Sin URL pública configurada — clic para añadir</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>

        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MiniStat label="Productos" value={stats.data.products} />
            <MiniStat label="Pedidos (total)" value={stats.data.orders.total} />
            <MiniStat label="Pedidos (mes)" value={stats.data.orders.thisMonth} />
            <MiniStat label="Clientes" value={stats.data.customers} />
          </div>
        )}

        {/* Plan & Usage */}
        {usage && (
          <Card id="usage-section">
            <CardHeader>
              <CardTitle>Plan & Uso del mes ({usage.data.period})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <UsageBar label="API requests" used={usage.data.usage.apiRequests.used} max={usage.data.usage.apiRequests.max} pct={usage.data.usage.apiRequests.pct} unit="" />
              <UsageBar label="Pedidos" used={usage.data.usage.orders.used} max={usage.data.usage.orders.max} pct={usage.data.usage.orders.pct} unit="" />
              <UsageBar label="Bandwidth" used={usage.data.usage.bandwidthGB.used} max={usage.data.usage.bandwidthGB.max} pct={usage.data.usage.bandwidthGB.pct} unit="GB" />
              <UsageBar label="Storage" used={usage.data.usage.storageGB.used} max={usage.data.usage.storageGB.max} pct={usage.data.usage.storageGB.pct} unit="GB" />
            </CardContent>
          </Card>
        )}

        {/* Info */}
        <Card>
          <CardHeader><CardTitle>Información</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Row label="ID" value={t.id} mono />
              <Row label="Slug" value={t.slug} mono />
              <Row label="Hostname interno" value={t.primaryHostname ?? '—'} mono />
              <Row label="Storefront URL" value={t.storefrontUrl ?? '—'} mono />
              <Row label="Status" value={t.status} />
              <Row label="Currency default" value={t.defaultCurrency} />
              <Row label="Locale default" value={t.defaultLocale} />
              <Row label="Creada" value={new Date(t.createdAt).toLocaleString()} />
              <Row label="Actualizada" value={new Date(t.updatedAt).toLocaleString()} />
            </dl>
            {Object.keys(t.metadata).length > 0 && (
              <div className="mt-4 pt-4 border-t border-neutral-800">
                <p className="text-xs text-neutral-500 mb-2 uppercase tracking-wider">Metadata</p>
                <pre className="text-xs font-mono bg-neutral-950 p-3 rounded border border-neutral-800 overflow-x-auto">
                  {JSON.stringify(t.metadata, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>

        {/* API keys */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>API keys</CardTitle>
              <Button size="sm" onClick={() => createKeyMutation.mutate()} disabled={createKeyMutation.isPending}>
                <Plus className="size-3 mr-2" /> Generar nueva
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!apiKeysData?.data.length ? (
              <p className="text-sm text-neutral-500">Sin API keys creadas.</p>
            ) : (
              <div className="space-y-2">
                {apiKeysData.data.map((k) => (
                  <div key={k.id} className="flex items-center justify-between py-2 border-b border-neutral-800 last:border-0">
                    <div className="flex items-center gap-3">
                      <Key className="size-4 text-neutral-500" />
                      <div>
                        <p className="font-mono text-sm">{k.prefix}***</p>
                        <p className="text-xs text-neutral-500">{k.name} · {k.environment}{k.lastUsedAt ? ` · usada ${new Date(k.lastUsedAt).toLocaleString()}` : ' · sin uso'}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => revokeKeyMutation.mutate(k.id)}>
                      Revocar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Storefront link if hostname */}
        {t.storefrontUrl && (
          <Card>
            <CardHeader><CardTitle>Storefront público</CardTitle></CardHeader>
            <CardContent>
              <a
                href={t.storefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
              >
                {t.storefrontUrl} <ExternalLink className="size-3" />
              </a>
              <p className="text-xs text-neutral-500 mt-2">
                URL configurada en Ajustes del store. El backoffice usa esta URL para "Ver en sitio" desde productos y blog posts.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {generatedKey && <RevealKeyDialog apiKey={generatedKey} onClose={() => setGeneratedKey(null)} />}
    </>
  )
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-neutral-500">{label}</dt>
      <dd className={mono ? 'font-mono text-neutral-200' : 'text-neutral-200'}>{value}</dd>
    </>
  )
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4 text-center">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-neutral-500 mt-1 uppercase tracking-wider">{label}</p>
      </CardContent>
    </Card>
  )
}

function UsageBar({ label, used, max, pct, unit }: { label: string; used: number; max: number; pct: number; unit: string }) {
  const danger = pct >= 90
  const warn = pct >= 70 && pct < 90
  const color = danger ? 'bg-red-500' : warn ? 'bg-yellow-500' : 'bg-green-500'
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-neutral-300">{label}</span>
        <span className="font-mono text-neutral-400">
          {used.toLocaleString()}{unit} / {max.toLocaleString()}{unit} <span className="text-neutral-500">({pct}%)</span>
        </span>
      </div>
      <div className="h-2 bg-neutral-800 rounded overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  )
}

function RevealKeyDialog({ apiKey, onClose }: { apiKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="p-6 space-y-4">
          <h2 className="text-lg font-semibold">API key generada</h2>
          <p className="text-sm text-neutral-400">Solo se muestra una vez. Cópiala ahora.</p>
          <div className="bg-neutral-950 border border-neutral-800 rounded p-3 font-mono text-xs break-all">
            {apiKey}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(apiKey); setCopied(true); setTimeout(() => setCopied(false), 2000) }}>
              {copied ? <Check className="size-4 mr-2" /> : <Copy className="size-4 mr-2" />}
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
            <Button onClick={onClose}>Cerrar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
